/**
 * buildSceneModel - Adapter: Fabric-Snapshot + Garten-Settings → SceneModel
 *
 * Reine, WebGL-freie Logik (keine React-/three-/fabric-Imports). Die R3F-
 * Komponenten in components/Editor/Garden3D sind dünne Mapper SceneModel → Meshes.
 *
 * SceneModel-Vertrag (PLAN_3D_VISUALISIERUNG_598.md §3.2, erweitert in #600):
 * {
 *   ground:     { outline: [{x,z}], material } | null,
 *   areas:      [{ id, kind, polygon: [{x,z}], yOffset, material, geometry? }],
 *                     //   #684: geometry = { positions, indices } — Fläche übers
 *                     //   Terrain drapiert (yOffset ist dann schon eingebacken);
 *                     //   fehlt bei flachem Garten, Teich und Hochbeet-Erdfläche
 *   extrusions: [{ id, kind, footprint: [{x,z}], baseY, heightM, material,
 *                  castsShadow, roof?, openingPanels?, obb?, point?,
 *                  polyline?, bandWidthM?, bandGeometry? }],
 *                     //   #684: polyline/bandWidthM = Quelle offener Bänder
 *                     //   (Zaun/Mauer/Rasenkante); bandGeometry = daraus am Hang
 *                     //   gestufte { positions, indices, steps }. baseY/heightM/
 *                     //   footprint/groundY bleiben die Objektwerte — der
 *                     //   2D-Schattenrechner (#605/#631) liest ausschließlich die
 *   plants:     [{ id, growthType, archetype, evergreen, position: {x,z},
 *                  heightM, crownDiameterM, seed, bloomColor, castsShadow,
 *                  baseY?, groundY? }],  // #601; baseY = Standhöhe = Gelände (#602)
 *                     //   plus Gefäß-Oberkante (#686); groundY = nur der
 *                     //   Geländeanteil davon (wie extrusion.groundY)
 *   terrain:    { positions, indices, minY, maxY, nx, nz, boundsM } | null,  // #602:
 *                     //   Heightfield aus elevationAt (Semantik PLAN_TERRAIN_GEFAELLE_623 §2).
 *                     //   Höhenzonen kommen aus settings.heightDifferences, NICHT aus dem
 *                     //   Snapshot (serializeCanvasObjects filtert sie); null = flacher Garten
 *   meta:       { boundsM, northAngle, counts, warnings[] }
 * }
 *
 * Einheiten: Meter. Koordinaten: Canvas (x, y) → Welt (x, z), Höhe = y.
 * Objekte unsichtbarer Layer tragen visible:false im Snapshot (#466) und
 * werden übersprungen; Unbekanntes degradiert zu warnings[] (kein Crash).
 */

import { pointToWorld, pxToM, cmToM } from './units';
import {
  sampleObjectOutline,
  expandPolylineToBand,
  polygonArea,
  composeMatrix,
  multiplyMatrices,
} from './shapeSampling';
import {
  getMaterialSpec,
  getKindMaterial,
  getPondWaterSpec,
  getRoofMaterialSpec,
  getRoofColorSpec,
} from './materials3d';
import {
  buildBandGeometry,
  buildFenceSlats,
  stepBaseYAt,
  centerlineFromFootprint,
  projectOntoPolyline,
  planBandPieces,
  slicePolyline,
  MIN_LINTEL_M,
} from './bandGeometry';
import { buildPlantSceneEntry } from './plantArchetypes';
import {
  createElevationSampler,
  buildTerrainField,
  buildTerrainGeometry,
  drapePolygon,
  polygonCentroid,
  pointInPolygon,
  WATER_BELOW_TERRAIN_M,
} from './terrain';
import { GARDEN_OBJECT_TYPES_CONFIG, getBuildingRoofForm } from '../config/gardenObjectTypes';

const MIN_OUTLINE_VERTICES = 3;

// Dachform-Config-Werte (#689) → kurze roof.type-Namen der 3D-Meshes.
const DACHFORM_TO_ROOF_TYPE = { satteldach: 'sattel', pultdach: 'pult', flachdach: 'flach' };

// Firsthöhe eines Gebäude-Satteldachs: Traufhöhe + Faktor · kürzere Grundriss-
// Spanne (die Dachflächen spannen die kürzere Achse auf). Analog PULT_RISE_FACTOR
// im Mesh — das Gebäude hat, anders als das Gewächshaus, kein ridgeHeight-Feld.
const GABLE_RISE_FACTOR = 0.4;

// Terrassen-Überdachung (#693 Chunk 1B): Plattendicke + Stützenquerschnitt in
// Metern, und der Stützenton (schlanke Aluminium-/Metallpfosten). Die Dachplatte
// bekommt ihr Material aus dachMaterial (getRoofMaterialSpec).
export const TERRACE_ROOF_PLATE_M = 0.06; // Dachplattendicke (= Roof-Mesh-Box)
export const TERRACE_SUPPORT_SIZE_M = 0.08; // Stützen-Grundriss (8×8 cm)
const TERRACE_SUPPORT_MATERIAL = { color: '#9CA3AF', opacity: 1, transparent: false };
// Markise (#693 Phase 2): Tuchbespannung, warmer Sandton, blickdicht. Die
// Markise hängt an einer abgeleiteten Ankerkante und kragt über die Terrasse.
const TERRACE_AWNING_MATERIAL = { color: '#D8C9A3', opacity: 1, transparent: false };
// Pultneigung der Terrassenüberdachung: Anstieg = Tiefe · Faktor, gedeckelt.
// Der Wert wird als roof.riseM an das Mesh durchgereicht (dort ist er die
// einzige Quelle der Neigung) UND bestimmt, um wie viel die zwei Stützen der
// Hochseite länger sind — so bleibt beides zwingend gekoppelt.
const TERRACE_PULT_RISE_FACTOR = 0.12;
const TERRACE_PULT_RISE_CAP_M = 0.5;

// Gewächshaus-Typen mit flachem Dach (#700): Frühbeetkasten und fester
// Wintergarten. Alle übrigen (freistehend, Anlehn, Folientunnel, Tomatenhaus)
// behalten das Giebeldach.
const FLAT_GREENHOUSE_TYPES = new Set(['fruehbeet', 'wintergarten']);

// Muldentiefe je Teich-Typ in cm (#700 teich.type): greift nur, wenn keine
// ausdrückliche Tiefe gesetzt ist. Pool/Schwimmteich tief, Bachlauf/Quellstein
// flach; unbekannter Typ fällt auf den Config-Default (80 cm).
const POND_DEPTH_BY_TYPE_CM = {
  gartenteich: 60,
  fischteich: 100,
  schwimmteich: 150,
  pool: 150,
  bachlauf: 25,
  quellstein: 20,
};

/** Effektive Muldentiefe eines Teichs in cm: gesetzte Tiefe vor Typ-Default. */
function resolvePondDepthCm(data) {
  const raw = data?.depth;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return POND_DEPTH_BY_TYPE_CM[data?.type] ?? getConfigDefault('teich', 'depth');
}

// Lattenzaun-Profil je zaun_typ (#700): Lattenbreite + Lücke (Meter). Sichtschutz
// fehlt bewusst — er bleibt das geschlossene Band. Ohne gesetzten Typ greift der
// Config-Default (lattenzaun), damit Bestandszäune der Panel-Anzeige entsprechen.
const DEFAULT_ZAUN_TYP = 'lattenzaun';
const ZAUN_SLAT_BY_TYPE = {
  lattenzaun: { slatWidthM: 0.08, gapM: 0.05 },
  staketen: { slatWidthM: 0.04, gapM: 0.07 },
  jägerzaun: { slatWidthM: 0.05, gapM: 0.09 },
};

// Kompost-Profil je Typ (#700): Höhe (cm) + 3D-Ton. Kompost hat kein material-
// Select, daher kommt der Ton aus dem Typ. Ohne Typ greift der Config-Default
// (thermokomposter). Eine ausdrückliche Nutzerfarbe übersteuert den Ton.
const KOMPOST_BY_TYPE = {
  thermokomposter: { heightCm: 90, color: '#3F3F46' }, // geschlossener Thermobehälter
  schnellkomposter: { heightCm: 100, color: '#4B5563' },
  offener: { heightCm: 80, color: '#9A7B52' }, // Holzlattung
  komposthaufen: { heightCm: 55, color: '#5E4632' }, // offener Erdhaufen
  drahtkomposter: { heightCm: 80, color: '#9CA3AF' }, // Drahtgeflecht
};

// yOffset-Leiter für flache Flächen (0,5-cm-Schritte gegen Z-Fighting)
// Kompost ist seit #700 kein flaches Feld mehr, sondern eine Extrusion mit
// typ-abhängiger Höhe — deshalb hier nicht mehr gelistet.
export const AREA_Y_OFFSETS = {
  rasen: 0.005,
  terrasse: 0.01,
  weg: 0.015,
  beet: 0.025,
};

// Höhen-Default-Audit (#602): Rasenkante hat keine Höhen-Property (Linie,
// 3–15 cm breit) — als flaches Band war sie in 3D quasi unsichtbar. Ein
// Bordstein von 5 cm macht sie lesbar, ohne die 2D-UI anzufassen.
export const RASENKANTE_HEIGHT_M = 0.05;

// #602: Der Teich-Interim-Offset (+0,035 m über dem Boden, #600-Befund) ist
// Geschichte — jeder Teich aktiviert das Heightfield (Mulde), und der
// Terrain-Post-Pass setzt die Wasserfläche auf
// elevationWithoutPonds(Zentroid) − WATER_BELOW_TERRAIN_M: sichtbar in der
// Mulde, unter dem Umgebungsniveau, über dem Muldengrund (−POND_DEPTH_M).

// Standardbreiten (Meter) zum Aufweiten von Linien zu Bändern
export const BAND_WIDTHS_M = {
  zaun: 0.06,
  mauer: 0.25,
};

export const DOOR_PANEL_DEPTH_M = 0.16; // Grundriss-Symboltiefe (#611: 16 px)

/**
 * Tiefe eines Torflügels in einer echten Aussparung (#698).
 *
 * Solange das Tor ein eingelassenes Paneel war, MUSSTE es mindestens so tief
 * sein wie das Band — sonst verschwand es darin. In einer echten Öffnung
 * entfällt der Zwang, und der Flügel bekommt sein reales Maß (3–5 cm).
 * DOOR_PANEL_DEPTH_M bleibt für das Standalone-Tor ohne Parent.
 */
export const GATE_LEAF_DEPTH_M = 0.05;

// #686: Gefäße, auf deren Oberkante eine Pflanze steht statt auf dem Boden.
// Bewusst NICHT dabei: Beet (flach, korrekt auf Geländehöhe) und Teich (Fläche,
// keine Extrusion — Wasserpflanzen wurzeln am Muldengrund).
export const PLANT_VESSEL_KINDS = ['hochbeet', 'topf', 'blumenkasten', 'blumenampel'];

// Einsenkung unter die Gefäß-Oberkante: die Pflanze sitzt in der Erde, statt auf
// dem Rand zu schweben. Beim Hochbeet liegt die Erdfläche bei heightM + 0,01 —
// die Pflanze steckt also 2 cm darin.
export const PLANT_VESSEL_SINK_M = 0.03;

const MIN_AREA_M2 = 0.0004; // 2 cm × 2 cm — darunter gilt ein Polygon als degeneriert

const AREA_TYPE_IDS = ['weg', 'terrasse', 'rasen', 'beet'];

// #684: Flächen, die dem Gelände folgen. Bewusst NICHT dabei: 'teich' (Wasser
// ist eben) und 'erde' (Hochbeet-Deckel, gehört auf die Kastenkante).
const DRAPED_AREA_KINDS = AREA_TYPE_IDS;

/** Default eines NUMBER-Properties aus der zentralen Config (z. B. height). */
export function getConfigDefault(typeId, key) {
  const entry = Object.values(GARDEN_OBJECT_TYPES_CONFIG).find((t) => t.id === typeId);
  return entry?.properties?.mvp?.[key]?.defaultValue ?? null;
}

function getConfigCastsShadow(typeId) {
  const entry = Object.values(GARDEN_OBJECT_TYPES_CONFIG).find((t) => t.id === typeId);
  return entry?.castsShadow ?? false;
}

/** Höhen-/Zahlenwert in cm: Objektdaten vor Config-Default. */
function numberProp(data, typeId, key) {
  const value = data?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return getConfigDefault(typeId, key) ?? 0;
}

/** Rotationswinkel (Grad) aus der finalen Transform-Matrix (gruppen-sicher). */
function matrixAngleDeg(m) {
  return (Math.atan2(m[1], m[0]) * 180) / Math.PI;
}

/** Objekt-Zentrum (Meter) aus der finalen Transform-Matrix. */
function matrixCenterM(m, pixelsPerCm) {
  return { x: pxToM(m[4], pixelsPerCm), z: pxToM(m[5], pixelsPerCm) };
}

/**
 * Orientierte Bounding-Box (Meter) aus lokaler Größe + finaler Matrix —
 * exakt für Rechtecke/Ellipsen, brauchbare Näherung für Polygone (Dächer).
 */
function computeObb(obj, m, pixelsPerCm) {
  const scaleX = Math.abs(obj.scaleX ?? 1);
  const scaleY = Math.abs(obj.scaleY ?? 1);
  const localW = obj.width ?? (obj.rx ? obj.rx * 2 : 0);
  const localH = obj.height ?? (obj.ry ? obj.ry * 2 : 0);
  return {
    center: matrixCenterM(m, pixelsPerCm),
    angleDeg: matrixAngleDeg(m),
    widthM: pxToM(localW * scaleX, pixelsPerCm),
    depthM: pxToM(localH * scaleY, pixelsPerCm),
  };
}

/** Rechteck-Footprint (Meter) um ein Zentrum, gedreht um angleDeg. */
function rotatedRectFootprint(center, angleDeg, widthM, depthM) {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const hw = widthM / 2;
  const hd = depthM / 2;
  return [
    { x: -hw, z: -hd },
    { x: hw, z: -hd },
    { x: hw, z: hd },
    { x: -hw, z: hd },
  ].map((p) => ({
    x: center.x + p.x * cos - p.z * sin,
    z: center.z + p.x * sin + p.z * cos,
  }));
}

/**
 * Compute axis-aligned bounds of ground points (meters).
 */
export function computeBounds(points) {
  if (!points || points.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }

  const width = maxX - minX;
  const depth = maxZ - minZ;
  return {
    minX,
    maxX,
    minZ,
    maxZ,
    width,
    depth,
    centerX: minX + width / 2,
    centerZ: minZ + depth / 2,
    // Umkreis-Radius der Grundfläche — Basis für Kamera-/Zoom-Grenzen
    radius: Math.sqrt(width * width + depth * depth) / 2,
  };
}

/**
 * Terrassen-Überdachung „Terrassendach" (#693): Stützen + Dachplatte als
 * zusätzliche Extrusionen über der (weiterhin flachen) Terrassenfläche.
 *
 * Muster wie das Hochbeet (Fläche + Kasten): Die Terrasse bleibt eine Area,
 * hier kommen NUR die Höhenkörper dazu. Der Schatten fällt geschenkt ab —
 * eine aufgeständerte Dachplatte (baseY = Traufhöhe) projiziert ihren Footprint
 * sonnenversetzt (collectCasters, #291), das ist der Terrassen-Schatten.
 *
 * - Vier Stützen an den OBB-Ecken (schlanke Boxen, castsShadow:true, Fläche
 *   vernachlässigbar).
 * - Eine Dachplatte mit dem Terrassen-Footprint (folgt damit auch Polygon-/
 *   Bezier-Terrassen), baseY = Traufhöhe, castsShadow:true → der Schattenwerfer.
 * - Die Dachform (#689-Vokabular) sitzt als `roof`-Sub-Objekt auf der Platte:
 *   pult/sattel als geneigte bzw. giebelige Kappe über der OBB; flach kommt
 *   ohne Kappe aus — die flache Platte IST das Flachdach.
 *
 * Für nicht-rechteckige Terrassen sind OBB-Ecken und -Kappe die Bounding-Box-
 * Näherung (definiert, kein Absturz); die schattenrelevante Platte folgt dem
 * echten Footprint.
 */
function buildTerraceRoof(ctx, { obj, m, points, data, id, pixelsPerCm }) {
  const obb = computeObb(obj, m, pixelsPerCm);
  const eaveM = cmToM(numberProp(data, 'terrasse', 'dachHoehe')); // Traufhöhe
  const roofMaterial = getRoofMaterialSpec(data.dachMaterial);
  const roofType = DACHFORM_TO_ROOF_TYPE[data.dachform] ?? 'flach';

  // Ein Pultdach steigt zu einer Seite an — dort müssen die Stützen mitwachsen,
  // sonst schwebt die hohe Dachkante. rotatedRectFootprint liefert die Ecken in
  // fester Reihenfolge; die Ecken 0/1 liegen auf der lokalen −z-Kante, und
  // exakt die ist im ExtrusionMesh-Pult die HOCHSEITE (Mesh-Gruppe und
  // Footprint teilen dieselbe Lokal→Welt-Abbildung). Sattel-/Flachdach sitzen
  // mit ihrer Traufe rundum auf Traufhöhe.
  const pultRiseM =
    roofType === 'pult'
      ? Math.min(TERRACE_PULT_RISE_CAP_M, obb.depthM * TERRACE_PULT_RISE_FACTOR)
      : 0;
  const cornerIsHigh = [true, true, false, false];

  // Stützen an den vier OBB-Ecken
  const corners = rotatedRectFootprint(obb.center, obb.angleDeg, obb.widthM, obb.depthM);
  corners.forEach((corner, i) => {
    ctx.extrusions.push({
      id: `${id}-stuetze-${i}`,
      kind: 'terrasse-stuetze',
      footprint: rotatedRectFootprint(
        corner,
        obb.angleDeg,
        TERRACE_SUPPORT_SIZE_M,
        TERRACE_SUPPORT_SIZE_M
      ),
      baseY: 0,
      heightM: eaveM + (cornerIsHigh[i] ? pultRiseM : 0),
      material: { ...TERRACE_SUPPORT_MATERIAL },
      castsShadow: true,
    });
  });

  // Dachplatte über der Terrasse (der eigentliche Schattenwerfer)
  const plate = {
    id: `${id}-dach`,
    kind: 'terrasse-dach',
    footprint: points,
    baseY: eaveM,
    heightM: TERRACE_ROOF_PLATE_M,
    material: roofMaterial,
    castsShadow: true,
  };
  // flach kommt ohne Kappe aus — die flache Platte selbst IST das Flachdach.
  // pult/sattel setzen die geneigte bzw. giebelige Kappe DIREKT auf die Stützen
  // (roof.baseY = Traufhöhe); der flache Footprint-Körper wird dann NICHT
  // gezeichnet (hideBody), sonst stünde er als zweites Flachdach unter der
  // Kappe. Als schattenwerfender Footprint bleibt die Extrusion erhalten.
  if (roofType === 'pult' || roofType === 'sattel') {
    const roof = { type: roofType, obb, baseY: eaveM };
    if (roofType === 'pult') {
      // Einzige Neigungsquelle: das Mesh liest roof.riseM, die Hochseiten-
      // Stützen wachsen um denselben Wert — kein Auseinanderdriften möglich.
      roof.riseM = pultRiseM;
    }
    if (roofType === 'sattel') {
      roof.ridgeY = eaveM + GABLE_RISE_FACTOR * Math.min(obb.widthM, obb.depthM);
    }
    plate.roof = roof;
    plate.hideBody = true;
  }
  ctx.extrusions.push(plate);
}

/**
 * Terrassen-Überdachung „Markise" (#693 Phase 2): eine auskragende, geneigte
 * Platte an EINER Kante, ohne Stützwerk — anders als das Terrassendach.
 *
 * Ankerkante (Aufhängekante): ohne Cross-Object-Wissen über umliegende Gebäude
 * die LÄNGSTE Kante der (bounding-box-genäherten) Terrasse. Die Markise hängt
 * dort und kragt um `markiseAusfall` nach innen über die Fläche aus — genau die
 * Fläche, die sie beschatten soll. `castsShadow:true` liefert den Schatten
 * geschenkt (collectCasters, #291), wie schon beim Terrassendach.
 *
 * Die Platte ist ein Pultdach: die HOHE Seite (lokal −z, siehe ExtrusionMesh)
 * zeigt nach AUSSEN zur Ankerkante, die Platte fällt über die Terrasse ab — die
 * Neigung einer echten Markise (hoch an der Wand, tief am Ausfall-Ende). Der
 * schattenwerfende Footprint bleibt im SceneModel (hideBody: nur die geneigte
 * Kappe wird gezeichnet, nicht der flache Körper darunter).
 *
 * `markiseAusgefahren:false` (eingerollt) erzeugt weder Geometrie noch Schatten.
 */
function buildTerraceAwning(ctx, { obj, m, data, id, pixelsPerCm }) {
  // Eingefahren: keine Geometrie, kein Schatten (AK #693 Phase 2).
  if (data.markiseAusgefahren === false) return;

  const obb = computeObb(obj, m, pixelsPerCm);
  const ausfallM = cmToM(numberProp(data, 'terrasse', 'markiseAusfall'));
  const mountM = cmToM(numberProp(data, 'terrasse', 'markiseHoehe'));
  if (!(ausfallM > 0) || !(obb.widthM > 0) || !(obb.depthM > 0)) return;

  // Ankerkante = längere OBB-Kante. rotatedRectFootprint liefert die Ecken in
  // fester Reihenfolge; die −z-Kante (0→1) hat Länge widthM, die +x-Kante (1→2)
  // Länge depthM.
  const corners = rotatedRectFootprint(obb.center, obb.angleDeg, obb.widthM, obb.depthM);
  const useWidthEdge = obb.widthM >= obb.depthM;
  const a = useWidthEdge ? corners[0] : corners[1];
  const b = useWidthEdge ? corners[1] : corners[2];
  const edgeLenM = useWidthEdge ? obb.widthM : obb.depthM;
  const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };

  // Kantenrichtung e (Einheitsvektor) und Einwärts-Normale nIn (Kantenmitte →
  // Terrassenzentrum, für eine OBB-Kante exakt senkrecht zur Kante).
  let ex = b.x - a.x;
  let ez = b.z - a.z;
  const eLen = Math.hypot(ex, ez) || 1;
  ex /= eLen;
  ez /= eLen;
  let nx = obb.center.x - mid.x;
  let nz = obb.center.z - mid.z;
  const nLen = Math.hypot(nx, nz) || 1;
  nx /= nLen;
  nz /= nLen;

  // e so orientieren, dass die HOHE Pultseite (lokal −z, Weltrichtung (ez,−ex))
  // nach AUSSEN zeigt (entgegen nIn) — sonst kippt die Markise verkehrt herum
  // (tief an der Wand, hoch über der Terrasse).
  if (ez * nx - ex * nz >= 0) {
    ex = -ex;
    ez = -ez;
  }

  // Footprint: Rechteck von der Ankerkante um Ausfall nach innen.
  const half = edgeLenM / 2;
  const p0 = { x: mid.x - ex * half, z: mid.z - ez * half };
  const p1 = { x: mid.x + ex * half, z: mid.z + ez * half };
  const p2 = { x: p1.x + nx * ausfallM, z: p1.z + nz * ausfallM };
  const p3 = { x: p0.x + nx * ausfallM, z: p0.z + nz * ausfallM };

  const awningObb = {
    center: { x: mid.x + nx * (ausfallM / 2), z: mid.z + nz * (ausfallM / 2) },
    angleDeg: (Math.atan2(ez, ex) * 180) / Math.PI,
    widthM: edgeLenM,
    depthM: ausfallM,
  };
  const riseM = Math.min(TERRACE_PULT_RISE_CAP_M, ausfallM * TERRACE_PULT_RISE_FACTOR);

  ctx.extrusions.push({
    id: `${id}-markise`,
    kind: 'terrasse-markise',
    footprint: [p0, p1, p2, p3],
    baseY: mountM,
    heightM: TERRACE_ROOF_PLATE_M,
    material: { ...TERRACE_AWNING_MATERIAL },
    castsShadow: true,
    roof: { type: 'pult', obb: awningObb, baseY: mountM, riseM },
    hideBody: true,
  });
}

/**
 * Umschlossenes Bauwerk (Wände bis Traufe + Dach-Kappe): die gemeinsame
 * Geometrie von Gewächshaus (#700) und Terrassen-Wintergarten (#693 Phase 3) —
 * Variante (b) aus dem Plan, EIN Pfad statt zweier driftender Umsetzungen. Beide
 * sind „verglaste Wände hoch, Dach drauf": ein Baukörper 0..wallM plus eine
 * roof-Kappe darüber.
 *
 * `roofType`:
 * - `flach`: der Körper reicht bis zur vollen Höhe, die Kappe liegt als dünne
 *   Platte auf wallM (keine Neigung, keine Traufe).
 * - `sattel`: Giebel-Kappe mit First auf `ridgeM`.
 * - `pult`: geneigte Kappe, Anstieg aus der OBB-Tiefe (gedeckelt), dieselbe
 *   Formel wie das Terrassendach.
 */
function buildEnclosureExtrusion({
  id,
  kind,
  footprint,
  obb,
  wallM,
  ridgeM,
  roofType,
  material,
  castsShadow,
}) {
  const roof = { type: roofType, obb, baseY: wallM };
  if (roofType === 'sattel') {
    roof.ridgeY = ridgeM;
  } else if (roofType === 'pult') {
    roof.riseM = Math.min(TERRACE_PULT_RISE_CAP_M, obb.depthM * TERRACE_PULT_RISE_FACTOR);
  }
  return {
    id,
    kind,
    footprint,
    baseY: 0,
    heightM: wallM,
    material,
    castsShadow,
    roof,
  };
}

/**
 * Terrassen-Überdachung „Wintergarten" (#693 Phase 3): ein raumbildendes,
 * verglastes Bauwerk auf der Terrasse. Anders als Terrassendach/Markise steht
 * hier kein aufgeständertes Dach, sondern echte Wände bis zur Wandhöhe plus Dach
 * — deshalb wirft der Baukörper einen Vollschatten (castsShadow:true), nicht nur
 * eine aufgeständerte Platte.
 *
 * Geometrie über den gemeinsamen buildEnclosureExtrusion-Pfad (wie das
 * Gewächshaus). Dachform/-material teilt der Wintergarten mit dem Terrassendach;
 * die Firsthöhe der Sattel-Kappe wird — mangels eigenem Ridge-Feld — aus Wandhöhe
 * + kürzerer Grundriss-Spanne abgeleitet (analog buildTerraceRoof/Gebäude).
 *
 * Für nicht-rechteckige Terrassen sind OBB und -Kappe die Bounding-Box-Näherung
 * (definiert, kein Absturz); der schattenwerfende Körper folgt dem Footprint.
 */
function buildTerraceConservatory(ctx, { obj, m, points, data, id, pixelsPerCm }) {
  const obb = computeObb(obj, m, pixelsPerCm);
  const wallM = cmToM(numberProp(data, 'terrasse', 'wintergartenWandhoehe'));
  if (!(wallM > 0)) return;
  // Ohne gesetzte Dachform greift das Satteldach — ein raumbildendes Bauwerk
  // liest sich mit Giebel natürlicher als mit Flachdach (anders als das
  // Terrassendach, dessen Default die flache Platte ist).
  const roofType = DACHFORM_TO_ROOF_TYPE[data.dachform] ?? 'sattel';
  const ridgeM = wallM + GABLE_RISE_FACTOR * Math.min(obb.widthM, obb.depthM);
  ctx.extrusions.push(
    buildEnclosureExtrusion({
      id: `${id}-wintergarten`,
      kind: 'terrasse-wintergarten',
      footprint: points,
      obb,
      wallM,
      ridgeM,
      roofType,
      material: getRoofMaterialSpec(data.dachMaterial),
      castsShadow: true,
    })
  );
}

// ---------------------------------------------------------------------------
// Objekt-Verarbeitung
// ---------------------------------------------------------------------------

/**
 * Ein serialisiertes Objekt in das SceneModel einsortieren.
 * ctx = { areas, extrusions, plants, linkedOpenings, extrusionsById, warnings, pixelsPerCm }
 */
function processObject(obj, ctx, parentMatrix = null) {
  const { warnings, pixelsPerCm } = ctx;

  if (obj?.visible === false) return; // Layer ausgeblendet (#466)

  if (obj?.objectType === 'plant') {
    // Pflanzen-Archetypen (#601): Position aus der finalen Matrix (gruppen-sicher)
    let pm = composeMatrix(obj);
    if (parentMatrix) pm = multiplyMatrices(parentMatrix, pm);
    const entry = buildPlantSceneEntry(obj, matrixCenterM(pm, pixelsPerCm), ctx.plants.length);
    if (entry) {
      ctx.plants.push(entry);
    } else if (!obj.plantProperties) {
      warnings.push('skipped:plant-no-data');
    }
    // lawn-Pflanzen liefern bewusst keinen Eintrag (Bodenmaterial, Plan §3.4)
    return;
  }

  const fabricType = (obj?.type || '').toLowerCase();
  const typeId = obj?.data?.objectType || obj?.data?.gardenObjectType;

  // Nutzer-Gruppen rekursiv auflösen (Kinder sind center-relativ zur Gruppe)
  if (fabricType === 'group' && !typeId) {
    let m = composeMatrix(obj);
    if (parentMatrix) m = multiplyMatrices(parentMatrix, m);
    (obj.objects || []).forEach((child) => processObject(child, ctx, m));
    return;
  }

  if (!typeId) {
    // Freie Zeichnen-Formen ohne Garten-Typ: bewusst nicht in 3D abgebildet
    warnings.push(`skipped:no-type:${fabricType || 'unknown'}`);
    return;
  }

  const data = obj.data || {};
  const id = data.objectId || `${typeId}-${ctx.areas.length + ctx.extrusions.length}`;

  let m = composeMatrix(obj);
  if (parentMatrix) m = multiplyMatrices(parentMatrix, m);

  // --- Öffnungen (Tor #611 / Fenster #690): Punkt-Objekt, Geometrie aus data ---
  if (typeId === 'tor' || typeId === 'fenster') {
    const kind = typeId;
    // Breite ist eine Fabric-Geometrie-Eigenschaft (obj.width·scaleX), keine
    // data-Property: das Panel schreibt sie via gate.set({ width }) — genau so,
    // wie gateLinkUtils sie liest (#690). Früher las 3D die Breite aus
    // data.width/data.widthCm, die zur Laufzeit nie aktualisiert werden →
    // Breitenänderungen kamen in der 3D-Ansicht nie an (Höhe/Brüstung schon,
    // die laufen über applyDataPatch nach data). Fallback auf die alten
    // data-Felder nur, falls das Objekt keine Geometrie trägt (z. B. Fixtures).
    const geomWidthPx = (obj.width || 0) * (obj.scaleX || 1);
    const widthM =
      geomWidthPx > 0
        ? pxToM(geomWidthPx, pixelsPerCm)
        : cmToM(numberProp(data, kind, 'width') || data.widthCm || 100);
    const heightM = cmToM(numberProp(data, kind, 'height'));
    // Fenster sitzen auf einer Brüstungshöhe über dem Boden (#690); Tore am Boden.
    const sillHeightM = kind === 'fenster' ? cmToM(numberProp(data, 'fenster', 'sillHeight')) : 0;
    const center = matrixCenterM(m, pixelsPerCm);
    const angleDeg = matrixAngleDeg(m);
    // Farbe für die 3D-Ansicht: Das Öffnungs-Panel trug bislang keine Farbe, das
    // Mesh setzte ein festes Kind-Material — die im Panel gewählte Farbe
    // (data.color) erreichte 3D nie. Jetzt reicht das Panel eine aufgelöste
    // Farb-Spec durch:
    //  - Tor: die gewählte Farbe gewinnt, sonst das Material (Holz/Metall …).
    //    getMaterialSpec braucht dafür useCustomColor; das Tor-Panel kennt keinen
    //    solchen Schalter, also leiten wir ihn aus „Nutzer hat eine Farbe gesetzt"
    //    ab (data.color ist bei Erstellung NICHT geseedet ⇒ != null = bewusst).
    //  - Fenster: die gewählte Farbe (bzw. Config-Default) ist die Rahmenfarbe;
    //    das Glas bleibt davon getrennt und fest (window-panel). Das Mesh rendert
    //    einen echten Rahmen in dieser Farbe um die Glasscheibe.
    const chosenColor = data.color ?? null;
    const frameSpec =
      kind === 'fenster'
        ? {
            color: chosenColor || getConfigDefault('fenster', 'color') || '#5B7C8A',
            opacity: 1,
            transparent: false,
          }
        : getMaterialSpec('tor', { ...data, useCustomColor: chosenColor != null });
    const panel = {
      id,
      kind,
      t: data.link?.t ?? null,
      edgeIndex: data.link?.edgeIndex ?? null,
      widthM,
      heightM,
      sillHeightM,
      center,
      angleDeg,
      frameSpec,
    };
    if (data.link?.parentId) {
      ctx.linkedOpenings.push({ parentId: data.link.parentId, panel });
    } else if (kind === 'tor') {
      // Standalone-Tor: dünne Extrusion wie Zaun, farblich abgesetzt
      ctx.extrusions.push({
        id,
        kind: 'tor',
        footprint: rotatedRectFootprint(center, angleDeg, panel.widthM, DOOR_PANEL_DEPTH_M),
        baseY: 0,
        heightM,
        material: frameSpec,
        castsShadow: getConfigCastsShadow('tor'),
      });
    } else {
      // Fenster ohne verlinktes Elternobjekt: ein Glaspaneel frei in der Luft
      // ergibt keinen Sinn — überspringen statt darzustellen (#690).
      warnings.push(`window-parent-missing:${id}`);
    }
    return;
  }

  // --- Blumenampel: Kugel auf mountHeight (Punkt-Geometrie) ---
  if (typeId === 'blumenampel') {
    const radiusM = cmToM(numberProp(data, 'blumenampel', 'diameter')) / 2 || 0.15;
    const mountM = cmToM(numberProp(data, 'blumenampel', 'mountHeight'));
    ctx.extrusions.push({
      id,
      kind: 'blumenampel',
      point: { center: matrixCenterM(m, pixelsPerCm), radiusM },
      baseY: Math.max(mountM - radiusM * 2, 0),
      heightM: radiusM * 2,
      material: getMaterialSpec('blumenampel', data),
      castsShadow: false,
    });
    return;
  }

  // --- Alle übrigen Typen: Kontur sampeln ---
  const sampled = sampleObjectOutline(obj, { pixelsPerCm, parentMatrix });
  if (!sampled) {
    warnings.push(`skipped:unsupported-shape:${typeId}:${fabricType}`);
    return;
  }
  let { points, closed } = sampled;

  // Offene Konturen (Linie, offener Bezier) zu Bändern aufweiten.
  // Die Quell-Polylinie wird dabei festgehalten: expandPolylineToBand macht ein
  // geschlossenes Band-Polygon daraus, in dem die Abschnitte nicht mehr
  // auffindbar sind — Teil B von #684 braucht sie aber zum Stufen.
  let polylineM = null;
  let bandWidthM = null;
  if (!closed) {
    if (typeId === 'weg' && data.shapeType === 'centerline') {
      // #720: Mittellinien-Weg — die Bandbreite kommt aus der Weg-Property
      // (data.width, cm), nicht aus der Stroke-Breite: so entspricht das
      // 3D-Band exakt der 2D-Ribbon-Breite (auch bei schmalen Wegen, die der
      // 0,4-m-Fallback-Boden unten sonst aufblähte), und der #696-Guard sieht
      // in weg.width einen echten 3D-Verbraucher.
      bandWidthM = cmToM(numberProp(data, 'weg', 'width'));
    } else if (typeId === 'rasenkante') {
      bandWidthM = cmToM(numberProp(data, 'rasenkante', 'width')) || 0.08;
    } else if (BAND_WIDTHS_M[typeId]) {
      bandWidthM = BAND_WIDTHS_M[typeId];
    } else {
      // z. B. offener Legacy-Bezier-Weg: Stroke-Breite, mindestens 40 cm begehbar
      bandWidthM = Math.max(pxToM(obj.strokeWidth ?? 0, pixelsPerCm), 0.4);
    }
    polylineM = points;
    points = expandPolylineToBand(points, bandWidthM);
    if (!points) {
      warnings.push(`skipped:degenerate:${typeId}`);
      return;
    }
  }

  if (polygonArea(points) < MIN_AREA_M2) {
    warnings.push(`skipped:degenerate:${typeId}`);
    return;
  }

  // --- Flache Flächen ---
  if (AREA_TYPE_IDS.includes(typeId)) {
    ctx.areas.push({
      id,
      kind: typeId,
      polygon: points,
      yOffset: AREA_Y_OFFSETS[typeId] ?? 0.01,
      material: getMaterialSpec(typeId, data),
    });
    // #693: Eine überdachte Terrasse bekommt zusätzlich Höhenkörper (die Fläche
    // selbst bleibt schattenneutral, nur die Extrusionen casten). Terrassendach:
    // Stützen + Dachplatte; Markise: auskragende geneigte Platte an einer Kante.
    if (typeId === 'terrasse') {
      if (data.ueberdachung === 'terrassendach') {
        buildTerraceRoof(ctx, { obj, m, points, data, id, pixelsPerCm });
      } else if (data.ueberdachung === 'markise') {
        buildTerraceAwning(ctx, { obj, m, points, data, id, pixelsPerCm });
      } else if (data.ueberdachung === 'wintergarten') {
        buildTerraceConservatory(ctx, { obj, m, points, data, id, pixelsPerCm });
      }
    }
    return;
  }

  if (typeId === 'teich') {
    // #691: Wasser nimmt standardmäßig die kuratierte getKindMaterial('wasser').
    // Eine ausdrücklich gesetzte Nutzerfarbe (useCustomColor) darf den Wasserton
    // übersteuern — ein dunkler oder türkiser Teich ist ein legitimer Wunsch —,
    // behält dabei aber die Wasser-Transparenz, damit die Fläche Wasser bleibt
    // statt zu einer opaken farbigen Platte zu werden (Entscheid 2026-08-07).
    // #700 teich.type: der Typ bestimmt den Wasserton (Pool türkis, Bachlauf
    // heller); eine Nutzerfarbe übersteuert ihn weiterhin.
    const waterSpec = getPondWaterSpec(data.type);
    ctx.areas.push({
      id,
      kind: 'teich',
      polygon: points,
      // #700 teich.depth: die Muldentiefe reicht der Terrain-Sampler pro Teich
      // durch (depthM), statt der früher global konstanten POND_DEPTH_M.
      depthM: cmToM(resolvePondDepthCm(data)),
      // Platzhalter — ein Teich aktiviert immer den Terrain-Post-Pass,
      // der das Wasser relativ zum lokalen Geländeniveau setzt (#602)
      yOffset: 0,
      material:
        data.useCustomColor && data.color
          ? { ...waterSpec, color: data.color }
          : waterSpec,
    });
    return;
  }

  // --- Extrusionen ---
  const castsShadow = getConfigCastsShadow(typeId);

  switch (typeId) {
    case 'rasenkante': {
      // Bordstein statt flachem Band (Höhen-Default-Audit #602)
      ctx.extrusions.push({
        id,
        kind: 'rasenkante',
        footprint: points,
        baseY: 0,
        heightM: RASENKANTE_HEIGHT_M,
        material: getMaterialSpec('rasenkante', data),
        castsShadow,
        polyline: polylineM,
        bandWidthM,
      });
      return;
    }

    case 'mauer':
    case 'zaun': {
      // Eine Mauer wird meist als RECHTECK gezogen (MAUER.allowedTools beginnt
      // mit RECTANGLE) und hat dann keine Polylinie. Geometrisch ist sie
      // trotzdem ein Band — die Mittellinie eines langgestreckten Vierecks ist
      // eindeutig (#684). Ein quadratischer Mauerklotz fällt durch das
      // Längenverhältnis heraus und behält den Ein-Anker-Ansatz.
      const derived = polylineM ? null : centerlineFromFootprint(points);
      const extrusion = {
        id,
        kind: typeId,
        footprint: points,
        baseY: 0,
        heightM: cmToM(numberProp(data, typeId, 'height')),
        material: getMaterialSpec(typeId, data),
        castsShadow,
        polyline: polylineM ?? derived?.polyline ?? null,
        bandWidthM: bandWidthM ?? derived?.widthM ?? null,
      };
      // #700 zaun_typ: Lattenzaun/Staketen/Jägerzaun werden zu Einzellatten
      // (applyFenceSlats, nach dem Terrain-Pass); Sichtschutz bleibt das Band.
      if (typeId === 'zaun') {
        const slatParams = ZAUN_SLAT_BY_TYPE[data.zaun_typ ?? DEFAULT_ZAUN_TYP];
        if (slatParams) {
          extrusion.slatFence = true;
          extrusion.slatParams = slatParams;
        }
      }
      ctx.extrusions.push(extrusion);
      if (data.objectId) ctx.extrusionsById.set(data.objectId, extrusion);
      return;
    }

    case 'kompost': {
      // #700 kompost.type: früher eine flache Fläche, jetzt eine Extrusion mit
      // typ-abhängiger Höhe + Ton. castsShadow bleibt der Config-Wert (false) —
      // kein Schattenwurf-Regressionsrisiko.
      const spec = KOMPOST_BY_TYPE[data.type] ?? KOMPOST_BY_TYPE.thermokomposter;
      const material =
        data.useCustomColor && data.color
          ? getMaterialSpec('kompost', data)
          : { color: spec.color, opacity: 1, transparent: false };
      ctx.extrusions.push({
        id,
        kind: 'kompost',
        footprint: points,
        baseY: 0,
        heightM: cmToM(spec.heightCm),
        material,
        castsShadow,
      });
      return;
    }

    case 'gebaeude': {
      const heightM = cmToM(numberProp(data, 'gebaeude', 'buildingHeight'));
      const obb = computeObb(obj, m, pixelsPerCm);
      // Dachform aus data.dachform, sonst aus dem Gebäude-Typ abgeleitet (#689).
      const roofType = DACHFORM_TO_ROOF_TYPE[getBuildingRoofForm(data)] ?? 'pult';
      const roof = { type: roofType, obb, baseY: heightM };
      if (roofType === 'sattel') {
        // Firsthöhe abgeleitet (kein ridgeHeight-Feld am Gebäude): Traufe + Neigung.
        roof.ridgeY = heightM + GABLE_RISE_FACTOR * Math.min(obb.widthM, obb.depthM);
      }
      // #722: Dach bekommt seine eigene Farbe (Default-Dachton als Fallback für
      // Alt-Objekte ohne das Feld). Der Dach-Mesh nutzt roof.material statt der
      // Wandfarbe; ohne roof.material fiele er auf die Wandfarbe zurück.
      roof.material = getRoofColorSpec(data.roofColor ?? getConfigDefault('gebaeude', 'roofColor'));
      const extrusion = {
        id,
        kind: 'gebaeude',
        footprint: points,
        baseY: 0,
        heightM,
        material: getMaterialSpec('gebaeude', data),
        castsShadow,
        roof,
      };
      ctx.extrusions.push(extrusion);
      if (data.objectId) ctx.extrusionsById.set(data.objectId, extrusion);
      return;
    }

    case 'gewaechshaus': {
      const ridgeM = cmToM(numberProp(data, 'gewaechshaus', 'ridgeHeight'));
      const obb = computeObb(obj, m, pixelsPerCm);
      // Typ bestimmt die Dachform (#700): Frühbeet/Wintergarten flach, Rest Giebel.
      // Beim Flachdach reicht der Baukörper bis zur vollen Firsthöhe (keine Traufe).
      const isFlat = FLAT_GREENHOUSE_TYPES.has(data.type);
      const wallM = isFlat ? ridgeM : ridgeM * 0.7; // Traufhöhe; First = ridgeHeight
      // Gemeinsamer Enclosure-Pfad mit dem Terrassen-Wintergarten (#693).
      const extrusion = buildEnclosureExtrusion({
        id,
        kind: 'gewaechshaus',
        footprint: points,
        obb,
        wallM,
        ridgeM,
        roofType: isFlat ? 'flach' : 'sattel',
        material: getMaterialSpec('gewaechshaus', data),
        castsShadow,
      });
      ctx.extrusions.push(extrusion);
      if (data.objectId) ctx.extrusionsById.set(data.objectId, extrusion);
      return;
    }

    case 'hochbeet': {
      const heightM = cmToM(numberProp(data, 'hochbeet', 'height'));
      ctx.extrusions.push({
        id,
        kind: 'hochbeet',
        footprint: points,
        baseY: 0,
        heightM,
        material: getMaterialSpec('hochbeet', data),
        castsShadow,
      });
      // Erdfläche oben (leicht über der Kastenkante)
      ctx.areas.push({
        id: `${id}-erde`,
        kind: 'erde',
        polygon: points,
        yOffset: heightM + 0.01,
        material: getKindMaterial('erde'),
      });
      return;
    }

    case 'blumenkasten': {
      ctx.extrusions.push({
        id,
        kind: 'blumenkasten',
        footprint: points,
        baseY: cmToM(numberProp(data, 'blumenkasten', 'mountHeight')),
        // Höhen-Default-Audit (#602): die VERTIKALE Dimension ist boxDepth
        // („Tiefe" des Kastens) — boxHeight ist in der Config die
        // horizontale Breite (Label „Breite", Grundriss = boxWidth×boxHeight)
        heightM: cmToM(numberProp(data, 'blumenkasten', 'boxDepth')),
        material: getMaterialSpec('blumenkasten', data),
        castsShadow,
      });
      return;
    }

    case 'topf': {
      ctx.extrusions.push({
        id,
        kind: 'topf',
        footprint: points,
        baseY: 0,
        heightM: cmToM(numberProp(data, 'topf', 'potHeight')),
        material: getMaterialSpec('topf', data),
        castsShadow,
      });
      return;
    }

    default:
      warnings.push(`skipped:unmapped-type:${typeId}`);
  }
}

// ---------------------------------------------------------------------------
// Gefäß-Zuordnung (#686)
// ---------------------------------------------------------------------------

/** Liegt die Pflanzenposition im Grundriss des Gefäßes? */
function vesselContains(vessel, position) {
  if (vessel.point) {
    // Blumenampel: Punkt-Geometrie ohne Footprint → Kreis um das Kugelzentrum
    const dx = position.x - vessel.point.center.x;
    const dz = position.z - vessel.point.center.z;
    return dx * dx + dz * dz <= vessel.point.radiusM * vessel.point.radiusM;
  }
  return Array.isArray(vessel.footprint) && pointInPolygon(position, vessel.footprint);
}

/**
 * Verlinkte Tore als echte Aussparung ins Band schneiden (#698).
 *
 * Die Öffnung wird als Bogenlängen-Intervall auf der Quell-Polylinie
 * ausgedrückt und trifft damit BEIDE Renderpfade:
 *
 *   - am Hang  → `bandGeometry` wird mit `openings` neu gebaut (Abschnitte)
 *   - flach    → `bandSegments` aus je Teilstück expandierten Footprints
 *
 * `extrusion.footprint` bleibt in beiden Fällen unangetastet. Das ist die
 * #684-Naht: Der 2D-Schattenrechner (#605/#631) liest genau den, und ein
 * geschlossenes Tor wirft zu Recht Schatten — die Lücke ist eine reine
 * Darstellungsfrage (Entscheid 2026-08-04).
 *
 * Läuft NACH dem Terrain-Post-Pass, weil `panel.baseY` erst dort auf den
 * Abschnitt unter dem Tor gesetzt wird — die Sturzunterkante hängt daran.
 * Ein Band ohne verlinktes Tor wird nicht angefasst.
 */
function applyGateOpenings(ctx, elevationAt) {
  for (const extrusion of ctx.extrusions) {
    const panels = extrusion.openingPanels;
    if (!panels?.length) continue;
    if (!extrusion.polyline || !(extrusion.bandWidthM > 0)) continue;
    // Lattenzäune schneiden ihre Öffnung über weggelassene Latten (applyFenceSlats).
    if (extrusion.slatFence) continue;

    const openings = [];
    for (const panel of panels) {
      // Nur Tore schneiden eine Aussparung ins Band; Fenster (an Volumen, nicht
      // an Bändern) erreichen diesen Pfad ohnehin nicht — Guard für die Zukunft.
      if (panel.kind === 'fenster') continue;
      const hit = projectOntoPolyline(extrusion.polyline, panel.center);
      if (!hit) continue;
      openings.push({
        startM: hit.s - panel.widthM / 2,
        endM: hit.s + panel.widthM / 2,
        headY: (panel.baseY ?? extrusion.baseY) + panel.heightM,
      });
      // Erst jetzt darf der Flügel schlank werden: ohne Aussparung müsste er
      // mindestens so tief sein wie das Band, sonst verschwände er darin.
      panel.inOpening = true;
    }
    if (openings.length === 0) continue;

    if (extrusion.bandGeometry) {
      const rebuilt = buildBandGeometry(extrusion.polyline, {
        widthM: extrusion.bandWidthM,
        heightM: extrusion.heightM,
        elevationAt,
        openings,
      });
      if (rebuilt) extrusion.bandGeometry = rebuilt;
      continue;
    }

    // Flacher Garten: dieselbe Zerlegung, nur als Footprints statt Abschnitte
    const segments = [];
    for (const piece of planBandPieces(extrusion.polyline, openings)) {
      const sub = slicePolyline(extrusion.polyline, piece.fromM, piece.toM);
      const polygon = sub && expandPolylineToBand(sub, extrusion.bandWidthM);
      if (!polygon) continue;

      const baseY = piece.floorY ?? extrusion.baseY;
      const heightM = extrusion.baseY + extrusion.heightM - baseY;
      if (piece.floorY != null && heightM < MIN_LINTEL_M) continue;
      segments.push({ polygon, baseY, heightM });
    }
    if (segments.length > 0) extrusion.bandSegments = segments;
  }
}

/**
 * Lattenzäune in Einzellatten auflösen (#700 zaun_typ).
 *
 * Läuft — wie applyGateOpenings — außerhalb des sampler.isActive-Blocks: die
 * Latten entstehen für flachen UND geneigten Garten (elevationAt liefert im
 * flachen Fall 0). Eine verlinkte Tor-Öffnung wird als Bogenlängen-Intervall
 * übergeben, damit im Torbereich keine Latte steht.
 *
 * `extrusion.footprint` bleibt unangetastet — der 2D-Schattenrechner behandelt
 * den Zaun weiter als geschlossenes Band (dieselbe #684-Naht wie beim Tor).
 */
function applyFenceSlats(ctx, elevationAt) {
  for (const extrusion of ctx.extrusions) {
    if (!extrusion.slatFence || !extrusion.polyline || !(extrusion.bandWidthM > 0)) continue;

    const openings = [];
    for (const panel of extrusion.openingPanels || []) {
      if (panel.kind === 'fenster') continue;
      const hit = projectOntoPolyline(extrusion.polyline, panel.center);
      if (!hit) continue;
      openings.push({ startM: hit.s - panel.widthM / 2, endM: hit.s + panel.widthM / 2 });
      panel.inOpening = true;
      // Tor auf die Geländehöhe an SEINER Position (die Latten stufen dort ebenso)
      // statt auf den Zaun-Anker — dieselbe Korrektur, die die Band-Stufung sonst
      // über stepBaseYAt vornimmt (die für Lattenzäune übersprungen wird).
      panel.baseY = elevationAt(panel.center);
    }

    // Die soliden Renderpfade sind für Lattenzäune ohnehin leer: die Band-Stufung
    // und applyGateOpenings überspringen slatFence-Extrusionen. Der Mesh-Pfad
    // priorisiert extrusion.slats vor Band/Footprint — kein Nullen nötig.
    extrusion.slats = buildFenceSlats(extrusion.polyline, {
      heightM: extrusion.heightM,
      depthM: extrusion.bandWidthM,
      slatWidthM: extrusion.slatParams.slatWidthM,
      gapM: extrusion.slatParams.gapM,
      elevationAt,
      openings,
    });
  }
}

/**
 * Pflanzen in Gefäßen auf deren Oberkante heben (#686).
 *
 * Die Zuordnung läuft über Containment der Position, nicht über eine
 * gespeicherte Verknüpfung: Der 2D-Editor kennt keine Eltern-Kind-Beziehung
 * zwischen Gefäß und Pflanze, und eine bei jedem Build neu berechnete Zuordnung
 * stimmt von allein wieder, wenn Gefäß oder Pflanze verschoben oder gelöscht
 * werden.
 *
 * Läuft NACH dem Terrain-Post-Pass (die Gefäß-baseY trägt dann schon das
 * Gelände) und — anders als jener — auch im flachen Garten.
 */
function liftPlantsOntoVessels(ctx) {
  const vessels = ctx.extrusions.filter((e) => PLANT_VESSEL_KINDS.includes(e.kind));
  if (vessels.length === 0) return;

  for (const plant of ctx.plants) {
    let best = null;
    for (const vessel of vessels) {
      if (!vesselContains(vessel, plant.position)) continue;
      // Bei Überlappung gewinnt die höchste Oberkante — physisch steht die
      // Pflanze auf dem obersten Gefäß.
      const topY = vessel.baseY + vessel.heightM;
      if (!best || topY > best.topY) best = { topY, groundY: vessel.groundY ?? 0 };
    }
    if (!best) continue;

    const standY = best.topY - PLANT_VESSEL_SINK_M;
    if (standY <= (plant.baseY ?? 0)) continue; // flaches Gefäß: nie unter den Boden ziehen
    plant.baseY = standY;
    // Geländeanteil des Gefäßes übernehmen: der Schattenrechner rechnet ihn
    // heraus, der Gefäß-Anteil bleibt stehen (#605-Muster, siehe shadowCaster).
    plant.groundY = best.groundY;
  }
}

// ---------------------------------------------------------------------------
// Einstieg
// ---------------------------------------------------------------------------

/**
 * Build the neutral SceneModel from a canvas snapshot and garden settings.
 *
 * @param {object|null} canvasJson - Ergebnis von serializeCanvasObjects(canvas)
 * @param {object} settings - GardenSettingsContext-Settings (outline, northAngle, …)
 * @param {object} [options]
 * @param {number} [options.pixelsPerCm=1] - Maßstab (Editor-Konvention: 1 px = 1 cm)
 * @returns {object} SceneModel
 */
export function buildSceneModel(canvasJson, settings, { pixelsPerCm = 1 } = {}) {
  const warnings = [];

  const vertices = settings?.outline?.vertices;
  let ground = null;
  if (Array.isArray(vertices) && vertices.length >= MIN_OUTLINE_VERTICES) {
    ground = {
      outline: vertices.map((v) => pointToWorld(v, pixelsPerCm)),
      material: 'lawn',
    };
  } else {
    warnings.push('no-outline');
  }

  const ctx = {
    areas: [],
    extrusions: [],
    plants: [],
    linkedOpenings: [],
    extrusionsById: new Map(),
    warnings,
    pixelsPerCm,
  };

  const canvasObjects = canvasJson?.objects ?? [];
  for (const obj of canvasObjects) {
    try {
      processObject(obj, ctx);
    } catch {
      // bewusst nur obj.type: data selbst kann die Fehlerquelle sein
      warnings.push(`skipped:error:${(obj?.type || 'unknown').toLowerCase()}`);
    }
  }

  // Verlinkte Öffnungen als openingPanels an ihre Struktur hängen (#611/#690 →
  // #600): eingelassenes, abgesetztes Paneel — keine Aussparung. Ein Tor ohne
  // auffindbaren Parent degradiert zur Standalone-Extrusion; ein Fenster wird
  // übersprungen (ein Glaspaneel frei in der Luft ergäbe keinen Sinn).
  for (const { parentId, panel } of ctx.linkedOpenings) {
    const parent = ctx.extrusionsById.get(parentId);
    if (parent) {
      parent.openingPanels = parent.openingPanels || [];
      parent.openingPanels.push({
        ...panel,
        parentHeightM: parent.heightM,
        // #684: Das Paneel richtet seine Tiefe an der Bandbreite aus — eine
        // feste Tiefe war an der 25-cm-Mauer stimmig und am 6-cm-Zaun ein Klotz.
        parentWidthM: parent.bandWidthM ?? null,
      });
    } else if (panel.kind === 'tor') {
      ctx.extrusions.push({
        id: panel.id,
        kind: 'tor',
        footprint: rotatedRectFootprint(panel.center, panel.angleDeg, panel.widthM, DOOR_PANEL_DEPTH_M),
        baseY: 0,
        heightM: panel.heightM,
        material: panel.frameSpec ?? getMaterialSpec('tor', {}),
        castsShadow: getConfigCastsShadow('tor'),
      });
      warnings.push(`gate-parent-missing:${panel.id}`);
    } else {
      warnings.push(`window-parent-missing:${panel.id}`);
    }
  }

  // --- Terrain (#602): Heightfield aus Settings-Zonen + Teich-Mulden ---
  // Der Sampler entsteht NACH dem Objekt-Loop (braucht die Teich-Polygone);
  // flache Gärten (isActive false) behalten terrain: null und damit den
  // bisherigen GroundMesh-Pfad — null-Regression.
  const boundsM = ground ? computeBounds(ground.outline) : null;
  // #700: Teiche mit ihrer eigenen Muldentiefe durchreichen. Die Struktur landet
  // identisch in meta.terrainSpec.ponds, aus dem der Schatten-Worker denselben
  // Sampler rekonstruiert (createShadowTerrain) — sonst desyncte die Tiefe.
  const ponds = ctx.areas
    .filter((a) => a.kind === 'teich')
    .map((a) => ({ polygon: a.polygon, depthM: a.depthM }));
  const sampler = createElevationSampler({
    heightDifferences: settings?.heightDifferences,
    outline: settings?.outline,
    ponds,
    pixelsPerCm,
  });

  // Das Feld (Höhen + Innen-Maske + Despike) entsteht EINMAL und wird danach
  // mehrfach geschnitten: an der Outline fürs Terrain, an jedem Flächen-Polygon
  // fürs Drapieren (#684). Nur so sitzen Fläche und Gelände auf demselben Raster.
  let terrain = null;
  let terrainField = null;
  if (ground && sampler.isActive) {
    terrainField = buildTerrainField(sampler.elevationAt, boundsM, ground.outline, {
      coverAt: sampler.zoneCoverAt, // offener-Boden-Despike (#629)
    });
    terrain = buildTerrainGeometry(sampler.elevationAt, boundsM, ground.outline, {
      field: terrainField,
    });
  }

  // --- Terrain-Post-Pass (#602): alles aufs Geländeniveau heben ---
  // v1: EIN Ankerpunkt pro Objekt (Zentroid/Fußpunkt), kein Verkippen.
  // WICHTIG für neue Objekttypen nach #602: Einträge müssen hier durchlaufen,
  // sonst schweben/versinken sie auf Plateaus.
  if (sampler.isActive) {
    const { elevationAt, elevationWithoutPonds } = sampler;

    for (const extrusion of ctx.extrusions) {
      const anchor = extrusion.point
        ? extrusion.point.center
        : polygonCentroid(extrusion.footprint);
      const elevation = elevationAt(anchor);
      if (elevation === 0) continue;
      extrusion.baseY += elevation;
      // Geländeanteil separat merken: der Schattenrechner (#605) rechnet ihn
      // wieder heraus (Schatten auf flachem Plan, kein Richtungs-Kippen im Gefälle).
      extrusion.groundY = elevation;
      if (extrusion.roof) {
        extrusion.roof.baseY += elevation;
        if (extrusion.roof.ridgeY != null) extrusion.roof.ridgeY += elevation;
      }
      for (const panel of extrusion.openingPanels || []) {
        // Paneele erben das Geländeniveau des Parents; die Fenster-Brüstung
        // (sillHeightM) kommt erst im Mesh obendrauf (#690).
        panel.baseY = elevation;
      }
    }

    // Bänder (Zaun, Mauer, Rasenkante) am Hang STUFEN statt zu drapieren (#684).
    // Läuft bewusst als eigener Durchgang nach dem Anker-Pass: baseY, heightM,
    // footprint und groundY bleiben dort auf den Objektwerten stehen — der
    // 2D-Schattenrechner (#605/#631) liest genau die und bleibt unberührt.
    for (const extrusion of ctx.extrusions) {
      if (!extrusion.polyline || !(extrusion.bandWidthM > 0)) continue;
      // Lattenzäune stufen sich über ihre Einzellatten (applyFenceSlats), nicht
      // über ein gestuftes Band.
      if (extrusion.slatFence) continue;
      const band = buildBandGeometry(extrusion.polyline, {
        widthM: extrusion.bandWidthM,
        heightM: extrusion.heightM,
        elevationAt,
      });
      if (!band) continue;
      extrusion.bandGeometry = band;
      for (const panel of extrusion.openingPanels || []) {
        // Tor auf die Höhe SEINES Abschnitts statt auf den Band-Anker (#611)
        const stepBase = stepBaseYAt(band.steps, panel.center);
        if (stepBase !== null) panel.baseY = stepBase;
      }
    }

    for (const area of ctx.areas) {
      const centroid = polygonCentroid(area.polygon);
      if (area.kind === 'teich') {
        // Wasserfläche: unter dem UMGEBUNGS-Niveau (ohne Mulde), sichtbar
        // in der Mulde — Muldengrund liegt bei −POND_DEPTH_M darunter.
        // Bleibt bewusst waagerecht: Wasser ist eben (#684).
        area.yOffset = elevationWithoutPonds(centroid) - WATER_BELOW_TERRAIN_M;
        continue;
      }
      // Drapieren (#684): Höhe pro Vertex statt EIN Anker. Die Leitersprosse
      // gegen Z-Fighting wird dabei in die Vertices eingebacken, area.yOffset
      // bleibt deshalb auf dem reinen Leiterwert stehen.
      // Nicht drapiert: die Hochbeet-Erdfläche ('erde') — sie gehört auf die
      // Kastenkante, nicht aufs Gelände, und teilt den Footprint mit dem Kasten.
      const geometry =
        terrainField && DRAPED_AREA_KINDS.includes(area.kind)
          ? drapePolygon(terrainField, area.polygon, area.yOffset)
          : null;
      if (geometry) {
        area.geometry = geometry;
      } else {
        // Fallback (kein Feld, Fläche außerhalb des Rasters, 'erde'): EIN Anker
        area.yOffset += elevationAt(centroid);
      }
    }

    for (const plant of ctx.plants) {
      const elevation = elevationAt(plant.position);
      if (elevation !== 0) {
        plant.baseY = elevation;
        plant.groundY = elevation;
      }
    }
  }

  // --- Tor-Aussparungen (#698): ebenfalls außerhalb von sampler.isActive ---
  // Der Grund ist derselbe wie beim Gefäß-Pass: `bandGeometry` entsteht NUR bei
  // aktivem Sampler, ein flacher Garten rendert das Band über extrudeGeometry.
  // Läge die Aussparung im Terrain-Block, gäbe es sie nur am Hang.
  applyGateOpenings(ctx, sampler.elevationAt);

  // --- Lattenzäune (#700): ebenfalls außerhalb von sampler.isActive ---
  applyFenceSlats(ctx, sampler.elevationAt);

  // --- Gefäß-Post-Pass (#686): läuft bewusst außerhalb von sampler.isActive ---
  // Ein Gefäß hebt die Pflanze auch im flachen Garten an.
  liftPlantsOntoVessels(ctx);

  return {
    ground,
    areas: ctx.areas,
    extrusions: ctx.extrusions,
    plants: ctx.plants,
    terrain,
    meta: {
      boundsM,
      northAngle: settings?.northAngle ?? 0,
      // Serialisierbare Terrain-Spezifikation (#631): der shadowCaster (Haupt-
      // Thread UND Heatmap-Worker) rekonstruiert daraus via createShadowTerrain
      // den analytischen Sampler — harte Plateau-Kanten bleiben scharf.
      terrainSpec: sampler.isActive
        ? {
            heightDifferences: settings?.heightDifferences ?? null,
            outline: settings?.outline ?? null,
            ponds,
            pixelsPerCm,
          }
        : null,
      counts: {
        canvasObjects: canvasObjects.length,
        areas: ctx.areas.length,
        extrusions: ctx.extrusions.length,
        plants: ctx.plants.length,
        terrain: sampler.zoneCount,
      },
      warnings,
    },
  };
}

export default buildSceneModel;
